/**
 * LAYOUT DE AUTENTICACIÓN CON LANDING BACKGROUND
 * 
 * Layout que carga la landing page de la app como fondo y muestra el formulario
 * de login en un contenedor flotante con efecto glassmorphism.
 * 
 * El branding se aplica automáticamente mediante useDynamicBranding hook.
 */

import React, { useState, useEffect } from 'react';
import { getBrandingConfig, BrandingConfig } from '@shared/config/brandingConfig';
import { useSearchParams } from 'react-router-dom';

/**
 * SPLASH SCREEN ANIMADO
 * Muestra el logo de la app y palabras rotativas mientras carga
 */
interface SplashScreenProps {
  branding: BrandingConfig;
  app: string | null;
}

const SplashScreen: React.FC<SplashScreenProps> = ({ branding, app }) => {
  const [currentWord, setCurrentWord] = useState(0);
  const words = branding.splash?.words || ['Cargando', 'Preparando', 'Conectando'];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWord((prev) => (prev + 1) % words.length);
    }, 600); // Cambia cada 600ms

    return () => clearInterval(interval);
  }, [words.length]);

  return (
    <div 
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-6"
      style={{ backgroundColor: `hsl(${branding.colors.background})` }}
    >
      {/* Logo con animación pulse */}
      {app === 'houseme' ? (
        <div className="flex items-center justify-center gap-3 animate-pulse">
          <img 
            src={branding.assets.logo || "/icon-houseme-192x192.png"} 
            className="h-20 w-20" 
            alt="HouseMe" 
          />
          <div className="text-3xl font-bold">
            <span style={{ color: `hsl(${branding.colors.primary})` }}>House</span>
            <span style={{ color: `hsl(${branding.colors.secondary || branding.colors.primary})` }}>Me</span>
          </div>
        </div>
      ) : app === 'empresa' ? (
        <div className="flex items-center justify-center gap-3 animate-pulse">
          <img 
            src={branding.assets.logo || "/icon-192x192.png"} 
            className="h-20 w-20" 
            alt="ThePlatform360" 
            onError={(e) => {
              // Fallback si el logo específico no existe
              const target = e.target as HTMLImageElement;
              if (target.src !== window.location.origin + '/icon-192x192.png') {
                target.src = '/icon-192x192.png';
              }
            }}
          />
          <div className="text-3xl font-bold" style={{ color: `hsl(${branding.colors.primary})` }}>
            ThePlatform360
          </div>
        </div>
      ) : branding.assets.logo ? (
        <img 
          src={branding.assets.logo} 
          className="h-20 animate-pulse" 
          alt={branding.label} 
        />
      ) : null}

      {/* Palabra animada */}
      <div className="text-center min-h-[40px] flex items-center justify-center">
        <p 
          key={currentWord}
          className="text-xl font-medium transition-all duration-300"
          style={{ 
            color: `hsl(${branding.colors.primary})`,
            animation: 'fadeInUp 0.4s ease-out'
          }}
        >
          {words[currentWord]}...
        </p>
      </div>

      {/* Dots animados */}
      <div className="flex gap-2">
        <div 
          className="w-2 h-2 rounded-full animate-bounce" 
          style={{ 
            backgroundColor: `hsl(${branding.colors.primary})`,
            animationDelay: '0ms' 
          }} 
        />
        <div 
          className="w-2 h-2 rounded-full animate-bounce" 
          style={{ 
            backgroundColor: `hsl(${branding.colors.primary})`,
            animationDelay: '150ms' 
          }} 
        />
        <div 
          className="w-2 h-2 rounded-full animate-bounce" 
          style={{ 
            backgroundColor: `hsl(${branding.colors.primary})`,
            animationDelay: '300ms' 
          }} 
        />
      </div>
    </div>
  );
};

interface AuthLayoutProps {
  children: React.ReactNode;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({ children }) => {
  const [params] = useSearchParams();
  const appName = params.get('app');
  const branding = getBrandingConfig(appName);

  // Lock scroll on auth pages only
  useEffect(() => {
    document.documentElement.classList.add('auth-page');
    return () => { document.documentElement.classList.remove('auth-page'); };
  }, []);
  
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);
  const [useIframe, setUseIframe] = useState(true); // Intentar iframe primero
  const [iframeError, setIframeError] = useState(false);
  const [isReady, setIsReady] = useState(false); // Control del splash screen
  
  // Determinar URL de landing (dev vs prod)
  const landingUrl = branding.landingUrl
    ? (import.meta.env.DEV && branding.landingUrl.dev
        ? branding.landingUrl.dev
        : branding.landingUrl.prod)
    : null;

  /** Evita iframe → misma SPA → otro iframe (recursión infinita en consola). */
  const shouldSkipLandingIframe =
    typeof window !== 'undefined' &&
    (window.self !== window.top ||
      (landingUrl != null &&
        (() => {
          try {
            return new URL(landingUrl, window.location.href).origin === window.location.origin;
          } catch {
            return true;
          }
        })()));
  
  // Fondo neutro mientras carga
  const backgroundStyle = {
    backgroundColor: '#ffffff', // Fondo blanco neutro
  };
  
  useEffect(() => {
    // Timeout para desactivar iframe si tarda mucho (probable CORS o error)
    const timeout = setTimeout(() => {
      if (!backgroundLoaded && !iframeError) {
        console.warn('[AuthLayout] Iframe timeout - usando gradiente de fondo');
        setUseIframe(false);
        setIframeError(true);
      }
    }, 2000); // Reducido a 2 segundos para respuesta más rápida
    
    return () => clearTimeout(timeout);
  }, [backgroundLoaded, iframeError]);

  // Control del splash screen: mostrar mínimo 1 segundo y esperar a que cargue
  useEffect(() => {
    const minLoadTime = 1000; // 1 segundo mínimo
    const startTime = Date.now();
    
    const checkReady = () => {
      const elapsed = Date.now() - startTime;
      // Esperar a que el iframe cargue O que falle, Y al menos 1 segundo
      if ((backgroundLoaded || iframeError) && elapsed >= minLoadTime) {
        setIsReady(true);
      } else if (backgroundLoaded || iframeError) {
        // Si ya cargó pero no pasó el tiempo mínimo, esperar lo que falta
        setTimeout(() => setIsReady(true), minLoadTime - elapsed);
      }
    };
    
    if (backgroundLoaded || iframeError) {
      checkReady();
    }
  }, [backgroundLoaded, iframeError]);
  
  // Handler para errores del iframe
  const handleIframeError = () => {
    console.warn('[AuthLayout] Iframe error - usando gradiente de fondo');
    setUseIframe(false);
    setBackgroundLoaded(false);
    setIframeError(true);
  };
  
  // Verificar si el iframe puede cargar (solo en producción o mismo origen)
  const canLoadIframe =
    landingUrl &&
    !shouldSkipLandingIframe &&
    (import.meta.env.PROD ||
      landingUrl.startsWith(window.location.origin) ||
      (landingUrl.includes('localhost') && window.location.hostname === 'localhost'));
  
  return (
    <>
      {/* Splash Screen - Se muestra hasta que todo esté listo */}
      {!isReady && <SplashScreen branding={branding} app={appName} />}
      
      {/* Contenido principal - Fade in cuando esté listo */}
      <div
        className="fixed inset-0 w-screen h-screen overflow-hidden transition-opacity duration-500"
        style={{
          ...backgroundStyle, // Fondo neutro basado en background de la app
          opacity: isReady ? 1 : 0,
          // position: 'fixed' viene de la clase 'fixed inset-0'
        }}
      >
      {/* Fondo: Landing Page (iframe) - Siempre visible, se carga en segundo plano */}
      {landingUrl && useIframe && canLoadIframe && !iframeError ? (
        <div
          className="absolute inset-0 overflow-hidden"
          style={{
            zIndex: 1,
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <iframe
            src={landingUrl}
            className="w-full h-full border-0"
            style={{
              pointerEvents: 'none', // No permitir interacción con el iframe
              opacity: 1, // Siempre visible, sin fade-in
              width: '100%',
              height: '100%',
              border: 'none',
              margin: 0,
              padding: 0,
              display: 'block',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
            scrolling="no" // Desactivar scroll
            frameBorder="0" // Sin borde
            onLoad={() => {
              console.log('[AuthLayout] Iframe cargado exitosamente');
              setBackgroundLoaded(true);
            }}
            onError={handleIframeError}
            title={`${branding.label} Landing Background`}
            sandbox="allow-same-origin allow-scripts allow-forms" // Seguridad: limitar capacidades del iframe
            loading="eager" // Cargar inmediatamente
          />
        </div>
      ) : null}
      
      {/* Overlay negro permanente con blur bajo para resaltar el formulario - SIEMPRE VISIBLE */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)', // Overlay negro más visible (aumentado a 0.6)
          backdropFilter: 'blur(4px)', // Blur un poco más pronunciado
          WebkitBackdropFilter: 'blur(4px)',
          zIndex: 2,
          pointerEvents: 'none', // No bloquear interacciones
        }}
      />
      
      {/* Patrón de fondo decorativo muy sutil (solo si no hay iframe) */}
      {(!landingUrl || iframeError || !useIframe) && (
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, hsl(var(--foreground) / 0.1) 0%, transparent 50%),
                             radial-gradient(circle at 80% 80%, hsl(var(--foreground) / 0.05) 0%, transparent 50%)`,
            zIndex: 1,
          }}
        />
      )}
      
      {/* Contenedor principal centrado */}
      <div
        className="auth-layout"
        style={{
          position: 'absolute', // Cambiar a absolute para centrado correcto sobre el fondo fijo
          zIndex: 10,
          height: '100vh', // Usar 100vh en lugar de 100% para altura completa del viewport
          width: '100vw', // Ancho completo del viewport
          top: 0, // Asegurar posición desde arriba
          left: 0, // Asegurar posición desde izquierda
          right: 0, // Asegurar posición desde derecha
          bottom: 0, // Asegurar posición desde abajo
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem', // Reducir padding en móvil (se ajusta con media query)
          pointerEvents: 'auto',
          margin: 0, // Asegurar sin márgenes
          boxSizing: 'border-box', // Incluir padding en el ancho
        }}
      >
        {/* Card flotante con glassmorphism mejorado */}
        <div
          className="auth-content"
          style={{
            backgroundColor: 'hsl(var(--card) / 0.95)', // ✅ Más opaco para mejor contraste
            borderRadius: '1rem', // ✅ Bordes redondeados más suaves (2xl = 1rem)
            padding: '2.5rem',
            maxWidth: '440px', // ✅ Aumentado de 340px a 440px (100px más)
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxSizing: 'border-box',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)', // ✅ Sombra más dramática
            backdropFilter: 'blur(20px)', // ✅ Blur más pronunciado para efecto glassmorphism
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid hsl(var(--border) / 0.3)',
            position: 'relative',
            pointerEvents: 'auto',
          }}
        >
          {children}
        </div>
      </div>
      </div>
    </>
  );
};

export default AuthLayout;
